import React, { useEffect, useRef } from "react";
import "./About.css";
import { Link } from "react-router-dom";

const About = () => {
  const imageRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Guard for older browsers or unusual environments
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      // Fallback: just reveal immediately
      if (imageRef.current) imageRef.current.classList.add("in-view");
      if (contentRef.current) contentRef.current.classList.add("in-view");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            // Unobserve this element after it becomes visible
            observer.unobserve(entry.target);
          }
        }
      },
      {
        root: null,
        threshold: 0.15,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    if (imageRef.current) observer.observe(imageRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // If team.jpeg is in /public, prefer PUBLIC_URL to avoid broken paths in production.
  // If the image is inside /src, import it: `import teamImg from "../assets/team.jpeg";` and use src={teamImg}
  const teamImg = `${process.env.PUBLIC_URL || ""}/team.jpeg`;

  return (
    <section className="About-home-section" id="about" aria-labelledby="about-title">
      <div className="About-home-container">
        <div className="About-home-inner">
          {/* Image Section */}
          <div className="About-home-image-container" ref={imageRef}>
            <div className="About-home-image-wrapper">
              <img
                src={teamImg}
                alt="Professional carpet cleaning team at work"
                className="About-home-image"
                loading="lazy"
                decoding="async"
              />
              <div className="About-home-image-overlay" aria-hidden="true"></div>
              <div className="About-home-image-shapes" aria-hidden="true">
                <div className="About-home-shape-circle"></div>
                <div className="About-home-shape-triangle"></div>
                <div className="About-home-shape-square"></div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="About-home-content" ref={contentRef}>
            <div className="About-home-content-wrapper">
              <div className="About-home-section-header">
                <h2 className="About-home-title" id="about-title">
                  <span className="About-home-title-text">About Us</span>
                  <span className="About-home-title-underline" aria-hidden="true"></span>
                </h2>
              </div>

              <div className="About-home-description">
                <p className="About-home-description-paragraph">
                  As a family-owned and locally operated carpet cleaning business in Seattle,
                  we are committed to delivering the highest quality service to every customer.
                  We use safe, non-toxic products that are gentle for your loved ones and pets,
                  ensuring a healthy home environment with every visit.
                </p>
                <p className="About-home-description-paragraph">
                  Our expertise goes beyond carpets—we also clean upholstery, tile &amp; grout,
                  and even RVs and campers. Booking is hassle-free: call us or fill out our quick
                  online form for an instant quote and convenient scheduling.
                </p>
              </div>

              <div className="About-home-stats-container" aria-label="Company stats">
                <div className="About-home-stat-item">
                  <div className="About-home-stat-value">12+</div>
                  <div className="About-home-stat-label">Years Experience</div>
                </div>
                <div className="About-home-stat-item">
                  <div className="About-home-stat-value">5,000+</div>
                  <div className="About-home-stat-label">Happy Clients</div>
                </div>
                <div className="About-home-stat-item">
                  <div className="About-home-stat-value">98%</div>
                  <div className="About-home-stat-label">Satisfaction Rate</div>
                </div>
              </div>

              <Link to="/about" className="About-home-cta-button" aria-label="Read more about us">
                <span className="About-home-button-text">About Us</span>
                <span className="About-home-button-icon" aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
